import React, { useRef, useState, useEffect, useMemo, Suspense } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Environment, Float } from "@react-three/drei";
import * as THREE from "three";
import Particles from "./Particles";
import BackgroundCaustics from "./BackgroundCaustics";
import { LogoIcon_FillFracture } from "./LogoIcon_Fill";
import ProjectBubbles from "./ProjectBubbles";
import Video from "./Video";
import SonicCup from "./SonicCup";

// Constants
const objectsDistance = 4;
let isPaused = true;

// Performance monitoring
const FPSMonitor = {
  frames: 0,
  lastTime: 0,
  fps: 60,
  updateFPS: function(now) {
    this.frames++;
    if (now - this.lastTime > 1000) {
      this.fps = this.frames;
      this.frames = 0;
      this.lastTime = now;
    }
    return this.fps;
  }
};

const Scene = ({ scrollY, sizes, onVideoSelect, modalOpen }) => {
  // Refs
  const cameraGroupRef = useRef();
  const logoIconRef = useRef();
  const frameSkipRef = useRef(0);
  const frustumRef = useRef(new THREE.Frustum());
  const projScreenMatrixRef = useRef(new THREE.Matrix4());

  const scrollEffect = (scrollY / sizes.height) * 3.6;

  // State
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const [targetCursor, setCursorTarget] = useState({ x: 0, y: 0 });
  const [prevScrollY, setPrevScrollY] = useState(0);
  const [scrollSpeed, setScrollSpeed] = useState(0);
  const [qualityLevel, setQualityLevel] = useState(1.0); // 0.0 = low, 1.0 = high
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollTimeout, setScrollTimeout] = useState(null);
  const [showSTIcon, setShowSTIcon] = useState(false); // Initially hide the ST Icon
  const [showIntro, setShowIntro] = useState(true); // Show intro animation
  const [showBubbles, setShowBubbles] = useState(true);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  // Get Three.js objects
  const { camera, scene, gl, clock } = useThree();

  // Track screen width for responsive rendering
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Handle cursor movement with debounce for better performance
  useEffect(() => {
    let lastMoveTime = 0;
    const moveThrottle = 16; // Only process mouse move every 16ms (approx 60fps)
    
    const handleMouseMove = (event) => {
      const now = performance.now();
      if (now - lastMoveTime < moveThrottle) return;
      
      lastMoveTime = now;
      setCursorTarget({
        x: event.clientX / sizes.width - 0.5,
        y: event.clientY / sizes.height - 0.5,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [sizes]);



  // Animation loop with performance optimizations
  useFrame((state, delta) => {
    // Smooth cursor movement with easing
    const cursorLerpFactor = 0.05; // Reduced for smoother easing
    setCursor({
      x: cursor.x + (targetCursor.x - cursor.x) * cursorLerpFactor,
      y: cursor.y + (targetCursor.y - cursor.y) * cursorLerpFactor
    });
    
    // Update FPS monitor
    const fps = FPSMonitor.updateFPS(state.clock.elapsedTime * 1000);
    
    // Adaptive quality based on FPS
    // If FPS drops below 40, reduce quality
    if (fps < 40 && qualityLevel > 0.5) {
      setQualityLevel(0.5);
    } else if (fps > 50 && qualityLevel < 1.0) {
      setQualityLevel(1.0);
    }
    
    // Skip frames for non-essential animations
    frameSkipRef.current = (frameSkipRef.current + 1) % 2;
    const skipFrame = frameSkipRef.current !== 0;
    
      
    // Calculate scroll speed (change in scroll position)
    const scrollDelta = Math.abs(scrollY - prevScrollY);
    
    // Detect if user is actively scrolling
    if (scrollDelta > 0.5) {
      setIsScrolling(true);
      
      // Clear any existing timeout
      if (scrollTimeout) clearTimeout(scrollTimeout);
      
      // Set a new timeout to detect when scrolling stops
      const timeout = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
      
      setScrollTimeout(timeout);
    }
    
    // Check if Bubbles should be unmounted (when scrolled beyond 6x)
    const bubblesMinThreshold = 0;
    const bubblesMaxThreshold = sizes.height * 6;
    if (showBubbles && (scrollY < bubblesMinThreshold || scrollY > bubblesMaxThreshold)) {
      setShowBubbles(false);
    } else if (!showBubbles && scrollY >= bubblesMinThreshold && scrollY <= bubblesMaxThreshold) {
      setShowBubbles(true);
    }
    
    
    // Apply smoothing to the scroll speed
    const scrollSensitivity = 6.5;
    const newScrollSpeed = scrollDelta * scrollSensitivity;
    setScrollSpeed(newScrollSpeed);
    setPrevScrollY(scrollY);
    
    // Update frustum for culling
    projScreenMatrixRef.current.multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse
    );
    frustumRef.current.setFromProjectionMatrix(projScreenMatrixRef.current);
    

    // Only apply parallax and scroll effects to main camera when not in spacewalk mode
    if (isPaused) {
      // Update camera position based on scroll
      camera.position.y = (-scrollY / sizes.height) * objectsDistance;

      // Ensure original position is maintained
      if (camera.userData && camera.userData.initialPosition) {
        camera.position.z = camera.userData.initialPosition.z;
      }

      // Apply parallax effect with reduced sensitivity during scrolling
      const parallaxFactor = isScrolling ? 0.3 : 0.5; // Reduce parallax effect during scrolling
      const parallaxX = cursor.x * parallaxFactor;
      const parallaxY = -cursor.y * parallaxFactor;

      // Apply parallax to camera group
      if (cameraGroupRef.current) {
        cameraGroupRef.current.position.x +=
          (parallaxX - cameraGroupRef.current.position.x) * 7 * delta;
        cameraGroupRef.current.position.y +=
          (parallaxY - cameraGroupRef.current.position.y) * 7 * delta;
      }
      
      
      // Apply parallax and rotation to LogoIcon with smoother positioning
      if (logoIconRef.current) {
        const iconRotationFactor = 0.8; // Keep rotation effect the same
        
        // Calculate scroll effect without additional damping
        const smoothScrollEffect = scrollEffect;
        
        // Simplified base position calculation with reduced z-movement
        const iconBasePosition = [0, -0.1 - smoothScrollEffect, 5 + smoothScrollEffect * 1.5]; 
        
        // Apply direct positioning without additional easing for y-position
        const targetX = iconBasePosition[0];
        const targetY = iconBasePosition[1]; // Remove additional y-movement
        const targetZ = iconBasePosition[2];
        
        // Calculate target rotation - keep the same
        const targetRotationX = cursor.y * iconRotationFactor; 
        const targetRotationY = cursor.x * iconRotationFactor;
        
        // Use direct position setting instead of spring-like motion
        logoIconRef.current.position.x = targetX;
        logoIconRef.current.position.y = targetY;
        logoIconRef.current.position.z = targetZ;
        
        // Keep smooth easing for rotation only
        const rotationEasingFactor = 0.03; // Use consistent rotation easing
        logoIconRef.current.rotation.x += (targetRotationX - logoIconRef.current.rotation.x) * rotationEasingFactor;
        logoIconRef.current.rotation.y += (targetRotationY - logoIconRef.current.rotation.y) * rotationEasingFactor;
      }
      
    }
  });

  return (
    <>
     
      {/* Camera Group */}
      <group ref={cameraGroupRef}></group>
      
      {/* Lights - adjusted for better water appearance */}
      <directionalLight position={[-1, 3, -8]} intensity={3} />
      <ambientLight intensity={0.2} />
      
      {/* Environment with brighter preset for better water reflections */}
      <Environment preset="sunset" background resolution={256} />
        

      {/* Water particles */}
      <Particles objectsDistance={objectsDistance} scrollY={scrollY} />
      
      {/* Background caustics */}
      <BackgroundCaustics />


      {/* Logo Icon */}
      <LogoIcon_FillFracture
          ref={logoIconRef}
          scrollY={scrollY} 
          sizes={sizes}
          cursor={cursor}
          position={[0, 0, 8]}
          animationStartThreshold={0.015} />

      {/* Bubbles with video texture - only render when in view and modal is not open */}
      {showBubbles && (
        <ProjectBubbles 
          scrollY={scrollY} 
          sizes={sizes} 
          cursor={cursor} 
          objectsDistance={objectsDistance}
          modalOpen={modalOpen}
          onBubbleClick={(index, config) => {
            // Only handle clicks when modal is not open
            if (!modalOpen) {
              console.log(`Bubble ${index} clicked:`, config);
              // Pass the project data to the parent component
              if (onVideoSelect) {
                // Replace _thumb.mp4 with .mp4 to get the full video path
                const fullVideoPath = config.videoPath.replace('_thumb.mp4', '.mp4');
                onVideoSelect(fullVideoPath, config.project);
              }
            }
          }}
        />
      )}

      {/* Sizzle Reel */}
      <Video 
          position={[0, -objectsDistance * 4.6, -5.0]} // Moved further back to account for larger scale
          scale={4.0} // Increased scale for larger video
          videoPath="/video/sizzle-reel_optimized.mp4"
          scrollY={scrollY}
          sizes={sizes}
          cursor={cursor}
          isVisible={scrollY > sizes.height * 2} // Increased from 6 to 8 to keep it mounted longer
          visibilityThreshold={0.4} // Adjust based on when you want the video to be active
        />
        {/* SonicCup Component - wrapped in a group with explicit render order */}
        <group>
          <SonicCup 
            scrollY={scrollY} 
            sizes={sizes} 
            cursor={cursor} 
            isScrolling={isScrolling} 
            qualityLevel={qualityLevel} 
          />
        </group>
        
    </>
  );
};

export default Scene;
